import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useContext, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { ToastContext } from '@/components/common/CommonToast';
import { useLoading } from '@/components/common/loader/LoadingContext';
import ButtonCustom from '@/components/common/ui/button';
import { useTranslation } from '@/config/i18n';
import useScrollToError from '@/hooks/useScrollToError';
import { toastErrorMessage, toastMessage } from '@/lib/utils';
import PATH from '@/routes/path';
import { apiRequest } from '@/services';
import DialogCustom from '@/components/common/ui/dialog';
import { InputTextCustomForm } from '@/components/common/ui/input/Input';
import { planFormSchema, PlanFormType } from '@/shared/schema/plan';
import {
  PLAN_NAME_MAX_LENGTH,
  planStatus,
  PRICE_MAX_LENGTH,
  STUDENT_LIMIT_MIN,
} from '@/shared/constants/plan';
import { HTTP_STATUS } from '@/shared/constants';

type Props = {
  id?: number;
};

const PlanForm: React.FC<Props> = ({ id }) => {
  const { toast } = useContext(ToastContext);
  const [t] = useTranslation('');
  const { apiService } = apiRequest();

  const navigate = useNavigate();
  const { scrollToError } = useScrollToError();
  const { loadingGlobal, setLoadingGlobal } = useLoading();
  const [dialogConfirmEditVisible, setDialogConfirmEditVisible] =
    useState(false);
  const [dataEdit, setDataEdit] = useState<PlanFormType>();
  const [statusEdit, setStatusEdit] = useState<string>();

  const formMethods = useForm<PlanFormType>({
    mode: 'onChange',
    resolver: yupResolver(planFormSchema),
    shouldFocusError: false,
    defaultValues: {
      name: '',
      price: '',
      student_limit: STUDENT_LIMIT_MIN,
    },
  });

  const {
    handleSubmit,
    clearErrors,
    formState: { errors },
    reset,
  } = formMethods;

  const getPlanDetail = useCallback(async (id: number) => {
    setLoadingGlobal(true);
    try {
      const { data: plan } = await apiService.plans.show(id);
      if (plan?.data) {
        reset({
          name: plan.data.name,
          price: plan.data.price || '',
          student_limit: plan.data.student_limit || STUDENT_LIMIT_MIN,
        });
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoadingGlobal(false);
    }
  }, [apiService, setLoadingGlobal]);

  function getPayload(data: PlanFormType, status: string) {
    return {
      status,
      name: data.name,
      price: data.price?.toString().replace(/,/g, '') ?? '',
      student_limit: data.student_limit,
    };
  }

  const handleShowDialogConfirmEdit = () => {
    setDialogConfirmEditVisible(prev => !prev);
  };

  async function onSavePlan(data: PlanFormType) {
    clearErrors();

    const payload = getPayload(data, planStatus.PUBLIC);

    if (id) {
      handleShowDialogConfirmEdit();
      setDataEdit(payload);
      setStatusEdit(planStatus.PUBLIC);

      return;
    }

    await handleSaveCreateGraduation(payload);
  }

  function handleNavigateBack() {
    navigate(PATH.plan);
  }

  function handleError(error: any) {
    const { status_code, errors, message } = error;

    if (status_code === HTTP_STATUS.FORM_ERROR) {
      errors.forEach((e: any) => {
        const { field } = e;
        if (field === 'name') {
          toast?.current?.show(
            toastErrorMessage(t('plan.plan_name_already_exists')),
          );
        }
      });
      return;
    }

    toast?.current?.show(toastErrorMessage(message));
  }

  async function handleSaveEditGraduation() {
    if (!id) return;
    setLoadingGlobal(true);

    const payload = { ...dataEdit, status: statusEdit };

    const { error: planError } = await apiService.plans.update(id, payload);

    if (planError) {
      handleError(planError);
      setLoadingGlobal(false);
      setDialogConfirmEditVisible(false);
      return;
    }

    toast?.current?.show(toastMessage(t('plan.edit_success')));
    handleNavigateBack();
  }

  async function handleSaveCreateGraduation(payload: any) {
    setLoadingGlobal(true);
    const { error: planError } = await apiService.plans.create(payload);

    if (planError) {
      handleError(planError);
      setLoadingGlobal(false);
      return;
    }

    toast?.current?.show(toastMessage(t('plan.save_success')));
    handleNavigateBack();
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      scrollToError();
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [errors]);

  useEffect(() => {
    if (id) {
      getPlanDetail(id);
    }
  }, [id, getPlanDetail]);

  return (
    <>
      <FormProvider {...formMethods}>
        <form
          className="mt-[16px]"
          autoComplete="off"
          onSubmit={handleSubmit(onSavePlan)}
        >
          <div className="form-item flex">
            <div className="label w-56 flex">
              <span>{t('plan.plan_name')}</span>
              <span className="text-error-500">*</span>
            </div>
            <InputTextCustomForm
              name="name"
              wrapClass="w-[512px]"
              placeholder={t('plan.plan_name_placeholder')}
              maxLength={PLAN_NAME_MAX_LENGTH}
            />
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('plan.amount')}</span>
              <span className="text-error-500">*</span>
            </div>
            <div className="w-[512px]">
              <InputTextCustomForm
                name="price"
                placeholder={t('plan.amount_placeholder')}
                isNumber
                preventStartZero
                convertComma
                maxLength={PRICE_MAX_LENGTH}
                prefixIcon={
                  <span className="text-[16px] leading-[19px] text-gray-700 absolute right-[10px] top-[12px]">
                    {t('plan.price_prefix')}
                  </span>
                }
              />
            </div>
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('plan.number_of_students')}</span>
              <span className="text-error-500">*</span>
            </div>
            <div className="w-[512px]">
              <InputTextCustomForm
                wrapClass="w-full"
                name="student_limit"
                isNumber
              />
            </div>
          </div>

          <div className="action flex justify-end mt-[25px] border-t border-gray-200 py-[18px] gap-5">
            <ButtonCustom
              className="w-[140px]"
              type="button"
              severity="secondary"
              onClick={() => handleNavigateBack()}
            >
              {t('plan.cancel_button')}
            </ButtonCustom>
            <ButtonCustom
              className="w-[140px]"
              type="submit"
            >
              {t('plan.save_button')}
            </ButtonCustom>
          </div>
        </form>
      </FormProvider>

      <DialogCustom
        header={t('plan.save_confirm.title')}
        visible={dialogConfirmEditVisible}
        className="w-[480px]"
        headerClassName="border-b border-gray-200 !py-[13px]"
        contentClassName="border-b border-gray-200 !p-[16px] !text-[16px] !leading-[24px] text-center text-gray-900 whitespace-pre-line"
        onHide={() => setDialogConfirmEditVisible(false)}
        footer={
          <div className="flex justify-end gap-4">
            <ButtonCustom
              type="button"
              className="!rounded-md"
              severity="secondary"
              onClick={() => setDialogConfirmEditVisible(false)}
            >
              {t('plan.save_confirm.cancel')}
            </ButtonCustom>
            <ButtonCustom
              type="button"
              className="!rounded-md"
              loading={loadingGlobal}
              onClick={async () => await handleSaveEditGraduation()}
            >
              {t('plan.save_confirm.save')}
            </ButtonCustom>
          </div>
        }
      >
        {t('plan.save_confirm.content')}
      </DialogCustom>
    </>
  );
};

export default PlanForm;
